import { Button, Checkbox, Modal } from '@resolute/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCookieConsent, type CookiePrefs } from './cookie-consent-context';

import { PANEL_TRANSITION } from '@/lib/motion';


/** Bottom banner (Accept all / Reject non-essential / Manage preferences) +
 *  a preferences modal with granular toggles. Mounted once in `PublicLayout`
 *  so it's available on every route. */
export function CookieBanner() {
  const { bannerOpen, prefs, acceptAll, rejectNonEssential, savePrefs } = useCookieConsent();
  const [managing, setManaging] = useState(false);
  const [draft, setDraft] = useState<CookiePrefs>(prefs);
  const bannerRef = useRef<HTMLDivElement>(null);
  const bannerVisible = bannerOpen && !managing;

  // Keep the draft in sync whenever the modal opens, so a previous cancelled
  // edit doesn't linger into the next time it's reopened.
  useEffect(() => {
    if (managing) setDraft(prefs);
  }, [managing, prefs]);

  // Move focus to the banner the moment it appears — as a fixed, non-modal
  // panel it doesn't otherwise interrupt page flow, so a keyboard/screen-
  // reader user could easily never notice it appeared (no aria-live region,
  // nothing else on the page changes). Landing focus here announces the
  // region + its text immediately, then Tab reaches the three actions
  // in order — the same pattern GOV.UK's cookie banner uses.
  useEffect(() => {
    if (bannerVisible) bannerRef.current?.focus();
  }, [bannerVisible]);

  return (
    <>
      <AnimatePresence>
        {bannerVisible && (
          <motion.div
            ref={bannerRef}
            tabIndex={-1}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={PANEL_TRANSITION}
            className="fixed inset-x-4 bottom-4 z-40 rounded-xl border border-border bg-card/95 shadow-xl backdrop-blur focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:inset-x-auto sm:left-4 sm:right-auto sm:w-full sm:max-w-sm"
            role="region"
            aria-label="Cookie consent"
          >
            <div className="flex flex-col gap-4 p-5">
              <p className="text-sm text-muted-foreground">
                We use essential local storage to run core features like your wishlist and compare
                list, and — only with your consent — analytics to understand how the site is used.
                Read our{' '}
                <Link to="/cookies" className="font-medium text-primary underline-offset-2 hover:underline">
                  Cookie Policy
                </Link>
                .
              </p>
              <div className="flex flex-col gap-2">
                <Button variant="primary" size="sm" onClick={acceptAll}>
                  Accept all
                </Button>
                <Button variant="outline" size="sm" onClick={rejectNonEssential}>
                  Reject non-essential
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setManaging(true)}>
                  Manage preferences
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        open={managing}
        onOpenChange={setManaging}
        title="Cookie preferences"
        description="Choose which cookies and similar local storage you're comfortable with. You can change this anytime from the Cookie Policy page."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setManaging(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                savePrefs(draft);
                setManaging(false);
              }}
            >
              Save preferences
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-5">
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm font-medium">Necessary</p>
              <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Always on
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Required for core features — your wishlist, compare list and this cookie preference
              itself. Stored in your browser only, never sent to our servers.
            </p>
          </div>

          <div>
            <Checkbox
              label="Analytics"
              checked={draft.analytics}
              onChange={(e) => setDraft((d) => ({ ...d, analytics: e.target.checked }))}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Helps us understand how the site is used so we can improve it. Off by default — no
              analytics run on this site unless you opt in here.
            </p>
          </div>

          <div>
            <Checkbox
              label="Marketing"
              checked={draft.marketing}
              onChange={(e) => setDraft((d) => ({ ...d, marketing: e.target.checked }))}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Not currently used on this site — reserved for future ad-personalisation features,
              off by default.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}
